import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';

import { useProvider } from '~/Hooks';
import { PROVIDER_SERVICE_URLS, USER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IStudio } from '~/Models/DTO/Studio.model';
import IUser from '~/Models/DTO/User.model';

export const useRequestOrgStudioList = (defaultQuery: { [name: string]: any }) => {
  // hooks
  const { currentProvider } = useProvider();
  const [responseHeaders, setResponseHeaders] = useState();
  const retrieveResponseHeader = useCallback(setResponseHeaders, []);

  const { excludedIds, ...params } = defaultQuery;

  const providerQuery = useQuery<IStudio[], IErrorResponse>(
    [REQUEST_ACTIONS.GET_ORG_STUDIO_LIST, defaultQuery, currentProvider?.organisationId],
    async () => {
      const data = await requestReactQuery<IStudio[]>({
        url: routeReplacer(PROVIDER_SERVICE_URLS.ORGANISATION_STUDIO_LIST, {
          organisationId: currentProvider?.organisationId || '0',
        }),
        params,
        config: { method: 'GET' },
        retrieveResponseHeader,
      });

      if (data?.length) {
        const dataWithOwners = await Promise.all(
          data?.map(async (studio) => {
            const ownerData = await requestReactQuery<{ user: IUser }>({
              url: routeReplacer(USER_SERVICE_URLS.USER_FROM_UUID, {
                uuid: studio.ownerUserUuid || '',
              }),
              config: { method: 'GET' },
            });
            return {
              ...studio,
              ownerEmail: ownerData?.user?.email,
              ownerFullName: `${ownerData?.user?.first_name} ${ownerData?.user?.last_name}`,
            };
          }),
        );
        return dataWithOwners;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: TIME_VALUES.HOUR,
    },
  );

  if (excludedIds) {
    providerQuery.data = providerQuery.data?.filter((studio) => !excludedIds.includes(studio.uuid));
    return { ...providerQuery, responseHeaders };
  }
  return { ...providerQuery, responseHeaders };
};
